<script setup lang="ts">
import {ref, set} from "vue"
import ListBrowserToolbar from "~components/admin/ListBrowserToolbar.vue"
import ListBrowserPagination from "~components/admin/ListBrowserPagination.vue"
import ExpandableJsonDetail from "~components/admin/ExpandableJsonDetail.vue"
import {useListBrowser} from "@/composables/useListBrowser"
import {formatDate, globParam, setQueryParam} from "@/common/utils/admin"

interface Entitlement {
  id: string
  user_id: number
  user_name: string | null
  user_email: string | null
  description: string | null
  stripe_payment_id: string | null
  stripe_price_id: string | null
  environment: string
  valid_from: string
  valid_until: string | null
  metadata: Record<string, unknown> | null
  created_at: string
  _showDetails?: boolean
}

const descriptionFilter = ref("")

const {
  dateFrom, dateTo, currentPage, perPage, sortBy, sortDesc,
  totalRows, rows, lastPage, doSearch, onSortChanged,
  setDateRange, dateRangeActive,
} = useListBrowser({
  serviceUrl: "/service/objects/entitlement",
  defaultSortColumn: "valid_from",
  dateColumn: "valid_from",
  buildFilterParams(params) {
    const desc = descriptionFilter.value.trim()
    if (desc) {
      setQueryParam(params, "description_glob", globParam(desc))
    }
  },
  readUrlParams(urlParams) {
    if (urlParams.has("description_glob")) {
      descriptionFilter.value = urlParams.get("description_glob")!.replace(/\*$/, "")
    }
  },
})

const fields = [
  {key: "valid_from", label: "Valid From", sortable: true, thStyle: {maxWidth: "10em"}},
  {key: "valid_until", label: "Valid Until", sortable: true, thStyle: {maxWidth: "10em"}},
  {key: "user_name", label: "User", sortable: false},
  {key: "user_email", label: "Email", sortable: false},
  {key: "description", label: "Package", sortable: true},
  {key: "stripe_payment_id", label: "Payment", sortable: false},
  {key: "environment", label: "Env", sortable: true, thStyle: {maxWidth: "4em"}},
  {key: "status", label: "Status", sortable: false, thStyle: {maxWidth: "5em"}},
]

const onRowClicked = (item: Entitlement) => set(item, "_showDetails", !item._showDetails)

const isActive = (item: Entitlement) => !!(item.valid_from && new Date(item.valid_from) <= new Date() && (!item.valid_until || new Date(item.valid_until) > new Date()))
const isUpcoming = (item: Entitlement) => item.valid_from && new Date(item.valid_from) > new Date()

const entitlementStatus = (item: Entitlement) => {
  if (isUpcoming(item)) return {text: "Upcoming", variant: "info"}
  if (isActive(item)) return {text: "Active", variant: "success"}
  return {text: "Expired", variant: "secondary"}
}

defineExpose({ descriptionFilter, dateFrom, dateTo, currentPage, perPage, sortBy, sortDesc, rows, fields, doSearch, onRowClicked, setDateRange, dateRangeActive, formatDate, isActive, entitlementStatus })
</script>

<template>
  <div>
    <!-- Filter bar -->
    <b-row class="mb-3 align-items-end">
      <b-col cols="12" sm="4" class="mb-2 mb-sm-0">
        <label class="mb-1 small text-muted">Description</label>
        <b-form-input
          v-model="descriptionFilter"
          placeholder="e.g. Tutoring Package"
          size="sm"
          @keydown.enter="doSearch()"
        />
      </b-col>
      <b-col cols="6" sm="2" class="mb-2 mb-sm-0">
        <label class="mb-1 small text-muted">Valid From</label>
        <b-form-input v-model="dateFrom" type="date" size="sm" @keydown.enter="doSearch()" />
      </b-col>
      <b-col cols="6" sm="2" class="mb-2 mb-sm-0">
        <label class="mb-1 small text-muted">Valid Until</label>
        <b-form-input v-model="dateTo" type="date" size="sm" @keydown.enter="doSearch()" />
      </b-col>
    </b-row>

    <list-browser-toolbar
      :date-range-active="dateRangeActive"
      @search="doSearch()"
      @set-date-range="setDateRange($event)"
    />

    <!-- Top pagination -->
    <list-browser-pagination
      :current-page="currentPage"
      :total-rows="totalRows"
      :per-page="perPage"
      :last-page="lastPage"
      @page-change="currentPage = $event; doSearch()"
      @per-page-change="perPage = $event; doSearch()"
    />

    <!-- Results -->

    <!-- Results -->
    <b-table
      :items="rows"
      :fields="fields"
      :sort-by="sortBy"
      :sort-desc="sortDesc"
      :current-page="currentPage"
      :per-page="perPage"
      primary-key="id"
      striped
      hover
      responsive
      show-empty
      empty-text="Click Search to load results"
      @row-clicked="onRowClicked"
      @sort-changed="onSortChanged"
    >
      <template #cell(valid_from)="data">
        <span class="date-cell">{{ formatDate(data.item.valid_from) }}</span>
      </template>
      <template #cell(valid_until)="data">
        <span class="date-cell" :class="{ 'text-muted': !data.item.valid_until }">{{ formatDate(data.item.valid_until) }}</span>
      </template>
      <template #cell(user_name)="data">
        <a
          v-if="data.item.user_id"
          :href="`/admin/users/edit/${data.item.user_id}`"
          target="_blank"
        >{{ data.item.user_name || '—' }}</a>
        <span v-else class="text-muted">—</span>
      </template>
      <template #cell(user_email)="data">
        {{ data.item.user_email || '—' }}
      </template>
      <template #cell(stripe_payment_id)="data">
        <a
          v-if="data.item.stripe_payment_id"
          :href="`https://dashboard.stripe.com/payments/${data.item.stripe_payment_id}`"
          target="_blank"
        >{{ data.item.stripe_payment_id }}</a>
        <span v-else class="text-muted">—</span>
      </template>
      <template #cell(description)="data">
        {{ data.item.description || '—' }}
      </template>
      <template #cell(environment)="data">
        <b-badge :variant="data.item.environment === 'production' ? 'primary' : 'secondary'">
          {{ data.item.environment === 'production' ? 'Prod' : 'Dev' }}
        </b-badge>
      </template>
      <template #cell(status)="data">
        <b-badge :variant="entitlementStatus(data.item).variant">
          {{ entitlementStatus(data.item).text }}
        </b-badge>
      </template>
      <template #row-details="data">
        <expandable-json-detail title="Entitlement Details" :source="data.item" />
      </template>
    </b-table>
  </div>
</template>
